import region1 from "../data/levels_region1_volcano.json";
import region2 from "../data/levels_region2_storm.json";
import region3 from "../data/levels_region3_ocean.json";
import region4 from "../data/levels_region4_earth.json";
import region5 from "../data/levels_region5_psychic.json";

// v3 — chaves compostas por região, unlockedByRegion, unlock em cascata
const STORAGE_KEY = "pazurumon_progress_v3";

const MONSTERS = ["fogo", "raio", "jato", "terra", "controlo"];
const XP_THRESHOLDS = { 2: 800, 3: 3000 }; // Lv2 aos 800, Lv3 aos 3000

export const REGIONS_ORDER = ["volcano", "storm", "ocean", "earth", "psychic"];

function defaultMonsters() {
  const m = {};
  for (const id of MONSTERS) m[id] = { xp: 0, level: 1 };
  return m;
}

function defaultProgress() {
  return {
    currentRegionId: "volcano",

    // Nível desbloqueado POR REGIÃO (sem cap em 30)
    unlockedByRegion: {
      volcano: 1,
      storm:   1,
      ocean:   1,
      earth:   1,
      psychic: 1,
    },

    // Regiões acessíveis (só volcano começa desbloqueada)
    regionUnlocked: {
      volcano: true,
      storm:   false,
      ocean:   false,
      earth:   false,
      psychic: false,
    },

    // Chave composta "regionId_levelId" — sem colisões entre regiões
    starsByLevel:     {},
    bestScoreByLevel: {},

    unlockedCreatures: [],
    monsters: defaultMonsters(),

    // Nível de Treinador — persistido entre sessões
    trainerLevel:   1,
    trainerXP:      0,
    trainerNextXP:  100,

    // Modo Infinito — desbloqueado ao completar todos os mundos
    infiniteModeUnlocked: false,
    infiniteBestScore:    0,
    infiniteBestWave:     1,
  };
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

/** Chave composta para evitar colisões entre regiões */
function levelKey(regionId, levelId) {
  return `${regionId}_${levelId}`;
}

export const LevelStore = {
  regions: {
    volcano: region1,
    storm:   region2,
    ocean:   region3,
    earth:   region4,
    psychic: region5,
  },

  getRegion(regionId = "volcano") {
    return this.regions[regionId];
  },

  getLevel(levelId, regionId = "volcano") {
    const region = this.getRegion(regionId);
    return region.levels.find((l) => l.id === levelId) || null;
  },

  loadProgress() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultProgress();
      const p = JSON.parse(raw);

      const base   = defaultProgress();
      const merged = { ...base, ...p };

      // Garantir campos novos (migração segura)
      merged.currentRegionId   = merged.currentRegionId || "volcano";
      merged.unlockedByRegion  = { ...base.unlockedByRegion,  ...(p.unlockedByRegion  || {}) };
      merged.regionUnlocked    = { ...base.regionUnlocked,    ...(p.regionUnlocked    || {}) };
      merged.starsByLevel      = { ...base.starsByLevel,      ...(p.starsByLevel      || {}) };
      merged.bestScoreByLevel  = { ...base.bestScoreByLevel,  ...(p.bestScoreByLevel  || {}) };

      if (!Array.isArray(merged.unlockedCreatures)) merged.unlockedCreatures = [];

      // Migração segura — trainerLevel/XP (campo novo)
      merged.trainerLevel  = Number(merged.trainerLevel)  || 1;
      merged.trainerXP     = Number(merged.trainerXP)     || 0;
      merged.trainerNextXP = Number(merged.trainerNextXP) || 100;

      // Migração segura — modo infinito
      merged.infiniteModeUnlocked = !!merged.infiniteModeUnlocked;
      merged.infiniteBestScore    = Number(merged.infiniteBestScore) || 0;
      merged.infiniteBestWave     = Number(merged.infiniteBestWave)  || 1;

      merged.monsters = { ...base.monsters, ...(p.monsters || {}) };
      for (const id of MONSTERS) {
        merged.monsters[id] = {
          xp:    Number(merged.monsters[id]?.xp    || 0),
          level: clamp(Number(merged.monsters[id]?.level || 1), 1, 3),
        };
      }

      return merged;
    } catch {
      return defaultProgress();
    }
  },

  saveProgress(progress) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  },

  // ── Helpers de leitura (usados em MapScene e LevelScene) ─────────────────

  getUnlockedLevel(regionId = "volcano") {
    return this.loadProgress().unlockedByRegion?.[regionId] || 1;
  },

  isRegionUnlocked(regionId) {
    return !!this.loadProgress().regionUnlocked?.[regionId];
  },

  getStars(levelId, regionId = "volcano") {
    return this.loadProgress().starsByLevel?.[levelKey(regionId, levelId)] || 0;
  },

  getBestScore(levelId, regionId = "volcano") {
    return this.loadProgress().bestScoreByLevel?.[levelKey(regionId, levelId)] || 0;
  },

  setCurrentRegion(regionId) {
    const p = this.loadProgress();
    p.currentRegionId = regionId;
    this.saveProgress(p);
  },

  // ── Registo de vitória ────────────────────────────────────────────────────

  recordWin({ levelId, regionId = "volcano", stars = 1, score = 0 }) {
    const p   = this.loadProgress();
    const key = levelKey(regionId, levelId);

    // Melhor score e estrelas por nível+região
    p.starsByLevel[key]    = Math.max(p.starsByLevel[key]    || 0, stars);
    p.bestScoreByLevel[key] = Math.max(p.bestScoreByLevel[key] || 0, score);

    // Avançar nível desbloqueado nesta região (sem cap)
    const current = p.unlockedByRegion[regionId] || 1;
    if (levelId >= current) {
      p.unlockedByRegion[regionId] = levelId + 1;
    }

    // Completou os 30 níveis desta região → desbloqueia a próxima
    if (levelId >= 30) {
      const idx = REGIONS_ORDER.indexOf(regionId);
      if (idx >= 0 && idx < REGIONS_ORDER.length - 1) {
        const nextRegion = REGIONS_ORDER[idx + 1];
        p.regionUnlocked[nextRegion] = true;
      }

      // Completou o último mundo → desbloqueia Modo Infinito
      if (regionId === REGIONS_ORDER[REGIONS_ORDER.length - 1]) {
        p.infiniteModeUnlocked = true;
      }
    }

    this.saveProgress(p);
    return p;
  },

  // ── Modo Infinito ─────────────────────────────────────────────────────────

  isInfiniteUnlocked() {
    return !!this.loadProgress().infiniteModeUnlocked;
  },

  getInfiniteBestScore() {
    return Number(this.loadProgress().infiniteBestScore) || 0;
  },

  getInfiniteBestWave() {
    return Number(this.loadProgress().infiniteBestWave) || 1;
  },

  saveInfiniteScore(score, wave = 1) {
    const p = this.loadProgress();
    if (score > (p.infiniteBestScore || 0)) {
      p.infiniteBestScore = score;
      p.infiniteBestWave  = wave;
      this.saveProgress(p);
      return true; // new record
    }
    return false;
  },

  // Sum of all best scores across every level — used by challenge leaderboard
  calcChallengeScore() {
    const p = this.loadProgress();
    return Object.values(p.bestScoreByLevel || {}).reduce((sum, s) => sum + Number(s), 0);
  },

  // ── XP dos monstros ───────────────────────────────────────────────────────

  applyMonsterXp(xpGainsByMonsterId) {
    const p = this.loadProgress();
    const leveledUp = [];

    for (const [id, gain] of Object.entries(xpGainsByMonsterId || {})) {
      if (!p.monsters[id]) continue;

      const beforeLevel = p.monsters[id].level;
      p.monsters[id].xp += Math.max(0, Number(gain || 0));

      let newLevel = 1;
      if (p.monsters[id].xp >= XP_THRESHOLDS[3]) newLevel = 3;
      else if (p.monsters[id].xp >= XP_THRESHOLDS[2]) newLevel = 2;

      newLevel = clamp(newLevel, 1, 3);
      p.monsters[id].level = newLevel;

      if (newLevel > beforeLevel) {
        leveledUp.push({ id, from: beforeLevel, to: newLevel });
      }
    }

    this.saveProgress(p);
    return { progress: p, leveledUp };
  },

  getMonsterLevel(monsterId) {
    return this.loadProgress().monsters?.[monsterId]?.level || 1;
  },

  getMonsterXp(monsterId) {
    return this.loadProgress().monsters?.[monsterId]?.xp || 0;
  },

  // ── Nível de Treinador ────────────────────────────────────────────────────
  getTrainerLevel()  { return this.loadProgress().trainerLevel  || 1;   },
  getTrainerXP()     { return this.loadProgress().trainerXP     || 0;   },
  getTrainerNextXP() { return this.loadProgress().trainerNextXP || 100; },

  saveTrainerProgress(level, xp, nextXP) {
    const p = this.loadProgress();
    p.trainerLevel  = level;
    p.trainerXP     = xp;
    p.trainerNextXP = nextXP;
    this.saveProgress(p);
  },

  getSkillMultiplier(monsterId) {
    const lvl = this.getMonsterLevel(monsterId);
    if (lvl === 2) return 2;
    if (lvl === 3) return 3;
    return 1;
  },

  resetProgress() {
    const p = defaultProgress();
    this.saveProgress(p);
    return p;
  },

  // ── Bestiário ─────────────────────────────────────────────────────────────

  unlockCreature(creatureName) {
    const p = this.loadProgress();
    if (!Array.isArray(p.unlockedCreatures)) p.unlockedCreatures = [];
    if (p.unlockedCreatures.includes(creatureName)) return false;
    p.unlockedCreatures.push(creatureName);
    this.saveProgress(p);
    return true;
  },

  hasCreature(creatureName) {
    return (this.loadProgress().unlockedCreatures || []).includes(creatureName);
  },

  getUnlockedCreatures() {
    return this.loadProgress().unlockedCreatures || [];
  },
};
